import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { DateAgoPipe } from './pipes/date-ago.pipe';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { provideAnimations } from '@angular/platform-browser/animations';
import { LayoutModule } from '@angular/cdk/layout';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MaterialModule } from './modules/material.module';
import { BatchesService } from './services/batches.services';
import { ProductsService } from './services/products.service';
import { NotificationService } from './services/notification.service';
import { SignalrService } from './services/signalr.service';
import { CommentService } from './services/comment.service';

import { DashboardComponent } from './components/home/dashboard/dashboard.component';
import { OutputCollectionsLatestComponent } from './components/home/output-collections/output-collections-latest/output-collections-latest.component';
import { ShootsLatestComponent } from './components/home/shoots/shoots-latest/shoots-latest.component';
import { PostProductionLatestComponent } from './components/home/post-production/post-production-latest/post-production-latest.component';
import { PostProductionReviewedLatestComponent } from './components/home/post-production/post-production-reviewed-latest/post-production-reviewed-latest.component';
import { ProductsRecentSectionComponent } from './components/home/products/products-recent-section/products-recent-section.component';
import { ProductListComponent } from './components/home/products/product-list/product-list.component';
import { BatchListComponent } from './components/home/batches/batch-list/batch-list.component';
import { HomeComponent } from './components/home/home.component';
import { LoginComponent } from './components/login/login.component';
import { BatchDetailsComponent } from './components/home/batches/batch-details/batch-details.component';
import { BatchCreateComponent } from './components/home/batches/batch-create/batch-create.component';
import { ProductCreateComponent } from './components/home/products/product-create/product-create.component';
import { ProductCreateUploadComponent } from './components/home/products/product-create-upload/product-create-upload.component';
import { ProductUpdateComponent } from './components/home/products/product-update/product-update.component';
import { ProductUpdateWarningComponent } from './components/home/products/product-list/product-update-warning/product-update-warning.component';
import { ProductReceivalsComponent } from './components/home/products/product-receivals/product-receivals.component';
import { DeleteModalComponent } from './components/shared/delete-modal/delete-modal.component';
import { EmailLogsComponent } from './components/shared/email-logs/email-logs.component';
import { EmailLogsPageComponent } from './components/home/admin/email-logs-page/email-logs-page.component';

// interceptors & auth-gaurd
import { HttpInterceptorProviders } from './interceptors/interceptor-provider';
import { AuthGuard } from './auth.guard';
import { PackageListComponent } from './components/home/packages/package-list/package-list.component';
import { PackageCreateComponent } from './components/home/packages/package-create/package-create.component';
import { PackageUpdateComponent } from './components/home/packages/package-update/package-update.component';
import { SettingListComponent } from './components/home/setting/setting-list/setting-list.component';
import { SettingCreateComponent } from './components/home/setting/setting-create/setting-create.component';

import { TitleCasePipe } from '@angular/common';
import { MasterSuiteListComponent } from './components/home/mastersuites/mastersuite-list/mastersuite-list.component';
import { MasterSuiteCreateComponent } from './components/home/mastersuites/mastersuite-create/mastersuite-create.component';
import { SettingUpdateComponent } from './components/home/setting/setting-update/setting-update.component';
import { MastersuiteUpdateComponent } from './components/home/mastersuites/mastersuite-update/mastersuite-update.component';
import { BatchUpdateComponent } from './components/home/batches/batch-update/batch-update.component';
import { ProductDetailsPageComponent } from './components/home/products/product-details-page/product-details-page.component';
import { ProductDetailItemComponent } from './components/home/products/product-details-page/product-detail-item/product-detail-item.component';
import { ProductProgressComponent } from './components/home/products/product-details-page/product-progress/product-progress.component';
import { StepPreProductionComponent } from './components/home/products/product-details-page/step-pre-production/step-pre-production.component';
import { StepProductionComponent } from './components/home/products/product-details-page/step-production/step-production.component';
import { StepPostProductionComponent } from './components/home/products/product-details-page/step-post-production/step-post-production.component';
import { StepOutputAssetsComponent } from './components/home/products/product-details-page/step-output-assets/step-output-assets.component';
import { OutputAssetsRangeViewComponent } from './components/home/products/product-details-page/output-assets-range-view/output-assets-range-view.component';
import { OutputAssetsEvViewComponent } from './components/home/products/product-details-page/output-assets-ev-view/output-assets-ev-view.component';
import { LifecyclePageComponent } from './components/home/products/product-details-page/lifecycle-page/lifecycle-page.component';
import { ReviewAssetsComponent } from './components/home/products/review-assets/review-assets.component';
import { ChatBoxComponent } from './components/shared/chat-box/chat-box.component';
import { CompareImageComponent } from './components/home/products/compare-image/compare-image.component';
import { AssetsNavigateComponent } from './components/home/products/review-assets/assets-navigate/assets-navigate.component';
import { CardItemComponent } from './components/shared/card-item/card-item.component';
import { SecurePipe } from './pipes/secure-asset.pipe';

import { OrganizationListComponent } from './components/home/organizations/organization-list/organization-list.component';
import { OrganizationCreateComponent } from './components/home/organizations/organization-create/organization-create.component';
import { OrganizationUpdateComponent } from './components/home/organizations/organization-update/organization-update.component';
import { OrganizationInfoComponent } from './components/home/organizations/organization-info/organization-info.component';
import { OrganizationDataConventionSectionComponent } from './components/home/organizations/organization-data-convention-section/organization-data-convention-section.component';
import { OrganizationShopifySyncComponent } from './components/home/organizations/organization-shopify-sync/organization-shopify-sync.component';
import { DataConventionUpdateComponent } from './components/home/data-conventions/data-convention-update/data-convention-update.component';
import { DataConventionSaveDialogComponent } from './components/home/data-conventions/data-convention-save-dialog/data-convention-save-dialog.component';
import { CreateDataConventionButtonComponent } from './components/home/data-conventions/create-data-convention-button/create-data-convention-button.component';
import { ProductDataFilenameConventionCardComponent } from './components/home/organizations/product-data-filename-conventions/product-data-filename-convention-card.component';
import { ProductDataFilenameConventionFormComponent } from './components/home/organizations/product-data-filename-conventions/product-data-filename-convention-form.component';
import { CreateProductDataFilenameConventionDialogComponent } from './components/home/organizations/product-data-filename-conventions/create-product-data-filename-convention-dialog.component';
import { UpdateProductDataFilenameConventionDialogComponent } from './components/home/organizations/product-data-filename-conventions/update-product-data-filename-convention-dialog.component';
import { ProductDataFilenameConventionHistoryComponent } from './components/home/organizations/product-data-filename-conventions/product-data-filename-convention-history.component';
import { OutputCompositionCardComponent } from './components/home/organizations/output-composition/output-composition-card.component';
import { OutputCompositionHistoryDialogComponent } from './components/home/organizations/output-composition/output-composition-history-dialog.component';
import { StillsFileSpecificationCreateDialogComponent } from './components/home/organizations/output-composition/file-specifications/stills-file-specification-create-dialog/stills-file-specification-create-dialog.component';
import { StillsFileSpecificationUpdateDialogComponent } from './components/home/organizations/output-composition/file-specifications/stills-file-specification-update-dialog/stills-file-specification-update-dialog.component';
import { VideosFileSpecificationCreateDialogComponent } from './components/home/organizations/output-composition/file-specifications/videos-file-specification-create-dialog/videos-file-specification-create-dialog.component';
import { VideosFileSpecificationUpdateDialogComponent } from './components/home/organizations/output-composition/file-specifications/videos-file-specification-update-dialog/videos-file-specification-update-dialog.component';
import { FileSpecificationDeleteDialogComponent } from './components/home/organizations/output-composition/file-specifications/file-specification-delete-dialog.component';
import { FileSpecificationsPanelComponent } from './components/home/organizations/output-composition/file-specifications/file-specifications-panel.component';
import { StillFormatConfigViewComponent } from './components/home/organizations/output-composition/file-specifications/still-format-config-view.component';
import { VideoFormatConfigViewComponent } from './components/home/organizations/output-composition/file-specifications/video-format-config-view.component';
import { RangeFilenameConventionCreateDialogComponent } from './components/home/organizations/output-composition/filename-conventions/range-filename-convention-create-dialog/range-filename-convention-create-dialog.component';
import { RangeFilenameConventionUpdateDialogComponent } from './components/home/organizations/output-composition/filename-conventions/range-filename-convention-update-dialog/range-filename-convention-update-dialog.component';
import { EvFilenameConventionCreateDialogComponent } from './components/home/organizations/output-composition/filename-conventions/ev-filename-convention-create-dialog/ev-filename-convention-create-dialog.component';
import { EvFilenameConventionUpdateDialogComponent } from './components/home/organizations/output-composition/filename-conventions/ev-filename-convention-update-dialog/ev-filename-convention-update-dialog.component';
import { FilenameConventionDeleteDialogComponent } from './components/home/organizations/output-composition/filename-conventions/filename-convention-delete-dialog.component';
import { RangeFilenameConventionEditorComponent } from './components/home/organizations/output-composition/filename-conventions/range-convention-editor/range-convention-editor.component';
import { EvConventionEditorComponent } from './components/home/organizations/output-composition/filename-conventions/ev-convention-editor/ev-convention-editor.component';
import { TokenChipPickerComponent } from './components/home/organizations/output-composition/shared/token-chip-picker/token-chip-picker.component';
import { FilenameConventionsPanelComponent } from './components/home/organizations/output-composition/filename-conventions/filename-conventions-panel.component';
import { OutputFormulaRangeCreateDialogComponent } from './components/home/organizations/output-composition/output-formulas/output-formula-range-create-dialog.component';
import { OutputFormulaEvCreateDialogComponent } from './components/home/organizations/output-composition/output-formulas/output-formula-ev-create-dialog.component';
import { OutputFormulaRangeUpdateDialogComponent } from './components/home/organizations/output-composition/output-formulas/output-formula-range-update-dialog.component';
import { OutputFormulaEvUpdateDialogComponent } from './components/home/organizations/output-composition/output-formulas/output-formula-ev-update-dialog.component';
import { OutputFormulaDeleteDialogComponent } from './components/home/organizations/output-composition/output-formulas/output-formula-delete-dialog.component';
import { OutputFormulasPanelComponent } from './components/home/organizations/output-composition/output-formulas/output-formulas-panel.component';
import { ReviewActionsComponent } from './components/home/products/review-assets/review-actions/review-actions.component';
import { ProductBulkIngestComponent } from './components/home/products/product-list/product-bulk-ingest/product-bulk-ingest.component';
import { CommentItemComponent } from './components/shared/chat-box/comment-item/comment-item.component';
import { PropertiesViewComponent } from './components/shared/chat-box/properties-view/properties-view.component';
import { DiscussionViewComponent } from './components/shared/chat-box/discussion-view/discussion-view.component';
import { AvatarComponent } from './components/shared/avatar/avatar.component';
import { MastersuiteTouchAssetCardComponent } from './components/home/mastersuites/mastersuite-content/mastersuite-touch-asset-card/mastersuite-touch-asset-card.component';
import { AvailableAssetCardComponent } from './components/home/mastersuites/enhancement-variant-create/available-asset-card/available-asset-card.component';
import { AutoSuggestionComponent } from './components/shared/auto-suggestion/auto-suggestion.component';
import { ProductSearchComponent } from './components/home/products/product-search/product-search.component';
import { ProductSearchItemComponent } from './components/home/products/product-search/product-search-item/product-search-item.component';
import { MentionModule } from 'angular-mentions';

import { UserListComponent } from './components/home/users/user-list/user-list.component';
import { UserCreateComponent } from './components/home/users/user-create/user-create.component';
import { UserUpdateComponent } from './components/home/users/user-update/user-update.component';
import { ChipAutocompleteComponent } from './components/home/organizations/chip-autocomplete/chip-autocomplete.component';
import { NotificationComponent } from './components/shared/notification/notification.component';
import { SelectAllCheckboxComponent } from './components/shared/select-all-checkbox/select-all-checkbox.component';
import { OutputFormulaPickerComponent } from './components/shared/output-formula-picker/output-formula-picker.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CreateOutputsCollectionComponent } from './components/home/output-collections/actions/create-outputs-collection.component';
import { CollectionProductDataFilesSelectionComponent } from './components/home/output-collections/actions/create-outputs-collection/collection-product-data-files-selection.component';
import { ReleaseOutputsCollectionActionComponent } from './components/home/output-collections/actions/release-outputs-collection-action/release-outputs-collection-action.component';
import { ProductActionsDropdownComponent } from './components/home/products/actions/product-actions-dropdown/product-actions-dropdown.component';
import { BatchActionsDropdownComponent } from './components/home/batches/actions/batch-actions-dropdown/batch-actions-dropdown.component';
import { CreateProductDataComponent } from './components/shared/create-product-data/create-product-data.component';
import { ProductLabelDataListComponent } from './components/shared/product-label-data-list/product-label-data-list.component';
import { ProductLabelDataDetailsComponent } from './components/shared/product-label-data-list/product-label-data-details/product-label-data-details.component';
import { ProductIdentifiersPopoverComponent } from './components/shared/product-identifiers-popover/product-identifiers-popover.component';
import { CreateCollectionWizardComponent } from './components/shared/create-collection-wizard/create-collection-wizard.component';
import { CreateCollectionWizardActionComponent } from './components/shared/create-collection-wizard/create-collection-wizard-action.component';
import { WizardStepperComponent } from './components/shared/wizard-stepper/wizard-stepper.component';
import { ReviewProductDataFilesDialogComponent } from './components/home/output-collections/review-product-data-files-dialog/review-product-data-files-dialog.component';
import { ReviewProductDataFilenamesDialogComponent } from './components/home/output-collections/review-product-data-filenames-dialog/review-product-data-filenames-dialog.component';
import { ReviewOutputFilenamesDialogComponent } from './components/home/output-collections/review-output-filenames-dialog/review-output-filenames-dialog.component';
import { ReviewOutputFilesDialogComponent } from './components/home/output-collections/review-output-files-dialog/review-output-files-dialog.component';
import { OutputAssetsReviewFilesDialogComponent } from './components/home/products/product-details-page/output-assets-review-files-dialog/output-assets-review-files-dialog.component';
import { TransferProductSettingsWizardComponent } from './components/home/products/transfer-product-settings-wizard/transfer-product-settings-wizard.component';
import { SelectMasterSuitesStepComponent } from './components/home/products/transfer-product-settings-wizard/select-master-suites-step/select-master-suites-step.component';
import { SelectProductsStepComponent } from './components/home/products/transfer-product-settings-wizard/select-products-step/select-products-step.component';
import { MapTouchesStepComponent } from './components/home/products/transfer-product-settings-wizard/map-touches-step/map-touches-step.component';
import { MapAssetsStepComponent } from './components/home/products/transfer-product-settings-wizard/map-assets-step/map-assets-step.component';
import { TouchMappingTableComponent } from './components/home/products/transfer-product-settings-wizard/touch-mapping-table/touch-mapping-table.component';
import { AssetMappingSubComponent } from './components/home/products/transfer-product-settings-wizard/asset-mapping-sub/asset-mapping-sub.component';
import { MastersuiteContentComponent } from './components/home/mastersuites/mastersuite-content/mastersuite-content.component';
import { EnhancementVariantContentComponent } from './components/home/mastersuites/mastersuite-content/enhancement-variant-content/enhancement-variant-content.component';
import { MasterSuiteDialogComponent } from './components/home/mastersuites/master-suite-dialog/master-suite-dialog.component';
import { CustomStepComponent } from './components/home/mastersuites/custom-step/custom-step.component';
import { EnhancementVariantCreateComponent } from './components/home/mastersuites/enhancement-variant-create/enhancement-variant-create.component';
import { WarningModalComponent } from './components/shared/warning-modal/warning-modal.component';
import { ConfirmModalComponent } from './components/shared/confirm-modal/confirm-modal.component';
import { PreferenceComponent } from './components/home/preference/preference.component';
import { BarChartComponent } from './components/home/dashboard/bar-chart/bar-chart.component';
import { DashboardCardComponent } from './components/home/dashboard/dashboard-card/dashboard-card.component';

import { ProductsQcViewComponent } from './components/home/products/product-list/products-qc-view/products-qc-view.component';
import { QcCardComponent } from './components/home/products/product-list/products-qc-view/qc-card/qc-card.component';
import { ReviewModalComponent } from './components/home/products/review-assets/review-modal/review-modal.component';
import { ReviewFullscreenComponent } from './components/home/products/review-assets/review-fullscreen/review-fullscreen.component';
import { SettingsVariantContentComponent } from './components/home/mastersuites/mastersuite-content/settings-variant-content/settings-variant-content.component';
import { MasterSuiteSettingsVaraintDialogComponent } from './components/home/products/actions/master-suite-settings-varaint-dialog/master-suite-settings-varaint-dialog.component';
import { TagsComponent } from './components/shared/tags/tags.component';
import { SelectOrganizationComponent } from './components/shared/select-organization/select-organization.component';
import { UnsavedChangesWarningComponent } from './components/shared/unsaved-changes-warning/unsaved-changes-warning.component';
import { SelectedAssetCardComponent } from './components/home/mastersuites/enhancement-variant-create/selected-asset-card/selected-asset-card.component';
import { DynamicChipSetComponent } from './components/shared/dynamic-chip-set/dynamic-chip-set.component';
import { ShopifyAuthResultComponent } from './components/home/organizations/shopify-auth-result/shopify-auth-result.component';
import { OutputsCollectionsService } from './services/outputs-collections.service';
import { ProductsTableComponent } from './components/shared/products-table/products-table.component';
import { ProductStatusCellComponent } from './components/shared/products-table/product-status-cell/product-status-cell.component';
import { OutputCollectionsListComponent } from './components/home/output-collections/output-collections-list/output-collections-list.component';
import { OutputCollectionsProductListComponent } from './components/home/output-collections/output-collections-product-list/output-collections-product-list.component';
import { CreateOutputsComponent } from './components/home/output-assets/actions/create-outputs/create-outputs.component';
import { GenerateShopifyAssetsSyncComponent } from './components/home/products/actions/generate-shopify-assets-sync/generate-shopify-assets-sync.component';
import { AssetsService } from './services/assets.service';
import {
  DynamicTableComponent,
  DynamicTableDialogComponent,
} from './components/shared/dynamic-table/dynamic-table.component';
import { GenerateAutoPostDialogComponent } from './components/home/products/actions/generate-auto-post-dialog/generate-auto-post-dialog.component';
import { ShootAssetArchiveDialogComponent } from './components/home/batches/actions/shoot-asset-archive-dialog/shoot-asset-archive-dialog.component';
import { ProductValidationListComponent } from './components/home/products/product-validation-list/product-validation-list.component';
import { ProductValidationDialogComponent } from './components/home/products/product-validation-list/product-validation-dialog.component';
import { LabelDataModule } from './components/home/label-data/label-data.module';
import { MasterSuiteActionsModule } from './components/home/mastersuites/actions/actions.module';
import { PostAssetsActionsModule } from './components/home/post-assets/actions/actions.module';
import { EnhancementVariantActionsModule } from './components/home/enhancement-variants/actions/actions.module';
import { LabelEditorComponent } from './components/home/label-data/label-editor/label-editor.component';
import { LabelDisplayComponent } from './components/home/label-data/label-display/label-display.component';
import { AssetUploadDialogComponent } from './components/shared/asset-upload-dialog/asset-upload-dialog.component';
import { ProductActionsModule } from './components/home/products/actions/actions.module';
import { BatchActionsModule } from './components/home/batches/actions/actions.module';
import { OutputAssetsActionsModule } from './components/home/output-assets/actions/actions.module';

@NgModule({
  declarations: [
    AppComponent,
    DashboardComponent,
    OutputCollectionsLatestComponent,
    ShootsLatestComponent,
    PostProductionLatestComponent,
    PostProductionReviewedLatestComponent,
    ProductsRecentSectionComponent,
    ProductListComponent,
    BatchListComponent,
    LoginComponent,
    HomeComponent,
    BatchDetailsComponent,
    BatchCreateComponent,
    ProductCreateComponent,
    ProductCreateUploadComponent,
    ProductUpdateComponent,
    ProductUpdateWarningComponent,
    ProductReceivalsComponent,
    DeleteModalComponent,
    EmailLogsComponent,
    EmailLogsPageComponent,
    DateAgoPipe,
    PackageListComponent,
    PackageCreateComponent,
    PackageUpdateComponent,
    SettingListComponent,
    SettingCreateComponent,
    MasterSuiteListComponent,
    MasterSuiteCreateComponent,
    SettingUpdateComponent,
    MastersuiteUpdateComponent,
    BatchUpdateComponent,
    ProductDetailsPageComponent,
    ProductDetailItemComponent,
    ProductProgressComponent,
    StepPreProductionComponent,
    StepProductionComponent,
    StepPostProductionComponent,
    StepOutputAssetsComponent,
    OutputAssetsRangeViewComponent,
    OutputAssetsEvViewComponent,
    LifecyclePageComponent,
    ReviewAssetsComponent,
    ChatBoxComponent,
    CompareImageComponent,
    AssetsNavigateComponent,
    CardItemComponent,
    SecurePipe,
    OrganizationListComponent,
    OrganizationCreateComponent,
    OrganizationUpdateComponent,
    OrganizationInfoComponent,
    OrganizationDataConventionSectionComponent,
    OrganizationShopifySyncComponent,
    DataConventionUpdateComponent,
    DataConventionSaveDialogComponent,
    CreateDataConventionButtonComponent,
    ProductDataFilenameConventionCardComponent,
    ProductDataFilenameConventionFormComponent,
    CreateProductDataFilenameConventionDialogComponent,
    UpdateProductDataFilenameConventionDialogComponent,
    ProductDataFilenameConventionHistoryComponent,
    OutputCompositionCardComponent,
    OutputCompositionHistoryDialogComponent,
    StillsFileSpecificationCreateDialogComponent,
    StillsFileSpecificationUpdateDialogComponent,
    VideosFileSpecificationCreateDialogComponent,
    VideosFileSpecificationUpdateDialogComponent,
    FileSpecificationDeleteDialogComponent,
    FileSpecificationsPanelComponent,
    StillFormatConfigViewComponent,
    VideoFormatConfigViewComponent,
    RangeFilenameConventionCreateDialogComponent,
    RangeFilenameConventionUpdateDialogComponent,
    EvFilenameConventionCreateDialogComponent,
    EvFilenameConventionUpdateDialogComponent,
    FilenameConventionDeleteDialogComponent,
    RangeFilenameConventionEditorComponent,
    EvConventionEditorComponent,
    TokenChipPickerComponent,
    FilenameConventionsPanelComponent,
    OutputFormulaRangeCreateDialogComponent,
    OutputFormulaEvCreateDialogComponent,
    OutputFormulaRangeUpdateDialogComponent,
    OutputFormulaEvUpdateDialogComponent,
    OutputFormulaDeleteDialogComponent,
    OutputFormulasPanelComponent,
    ReviewActionsComponent,
    ProductBulkIngestComponent,
    CommentItemComponent,
    PropertiesViewComponent,
    DiscussionViewComponent,
    AvatarComponent,
    MastersuiteTouchAssetCardComponent,
    AvailableAssetCardComponent,
    SelectedAssetCardComponent,
    AutoSuggestionComponent,
    ProductSearchComponent,
    ProductSearchItemComponent,
    UserListComponent,
    UserCreateComponent,
    UserUpdateComponent,
    ChipAutocompleteComponent,
    NotificationComponent,
    SelectAllCheckboxComponent,
    OutputFormulaPickerComponent,
    CreateOutputsCollectionComponent,
    CollectionProductDataFilesSelectionComponent,
    ReleaseOutputsCollectionActionComponent,
    ProductActionsDropdownComponent,
    BatchActionsDropdownComponent,
    CreateProductDataComponent,
    ProductLabelDataListComponent,
    ProductLabelDataDetailsComponent,
    ProductIdentifiersPopoverComponent,
    CreateCollectionWizardComponent,
    CreateCollectionWizardActionComponent,
    ReviewProductDataFilesDialogComponent,
    ReviewProductDataFilenamesDialogComponent,
    ReviewOutputFilenamesDialogComponent,
    ReviewOutputFilesDialogComponent,
    OutputAssetsReviewFilesDialogComponent,
    TransferProductSettingsWizardComponent,
    SelectMasterSuitesStepComponent,
    SelectProductsStepComponent,
    MapTouchesStepComponent,
    MapAssetsStepComponent,
    TouchMappingTableComponent,
    AssetMappingSubComponent,
    MastersuiteContentComponent,
    EnhancementVariantContentComponent,
    MasterSuiteDialogComponent,
    CustomStepComponent,
    EnhancementVariantCreateComponent,
    WarningModalComponent,
    ConfirmModalComponent,
    SelectOrganizationComponent,
    PreferenceComponent,
    BarChartComponent,
    DashboardCardComponent,
    ProductsQcViewComponent,
    QcCardComponent,
    ReviewModalComponent,
    SettingsVariantContentComponent,
    MasterSuiteSettingsVaraintDialogComponent,
    TagsComponent,
    UnsavedChangesWarningComponent,
    DynamicChipSetComponent,
    ReviewFullscreenComponent,
    ShopifyAuthResultComponent,
    OutputCollectionsListComponent,
    OutputCollectionsProductListComponent,
    ProductsTableComponent,
    ProductStatusCellComponent,
    CreateOutputsComponent,
    GenerateShopifyAssetsSyncComponent,
    DynamicTableComponent,
    DynamicTableDialogComponent,
    GenerateAutoPostDialogComponent,
    ShootAssetArchiveDialogComponent,
    ProductValidationListComponent,
    ProductValidationDialogComponent,
    LabelEditorComponent,
    LabelDisplayComponent,
    AssetUploadDialogComponent,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    AppRoutingModule,
    BrowserModule,
    LayoutModule,
    MaterialModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    MentionModule,
    ClipboardModule,
    ProductActionsModule,
    BatchActionsModule,
    OutputAssetsActionsModule,
    LabelDataModule,
    MasterSuiteActionsModule,
    PostAssetsActionsModule,
    EnhancementVariantActionsModule,
    WizardStepperComponent,
  ],
  providers: [
    AuthGuard,
    HttpInterceptorProviders,
    BatchesService,
    ProductsService,
    AssetsService,
    NotificationService,
    OutputsCollectionsService,
    SignalrService,
    CommentService,
    DatePipe,
    SecurePipe,
    TitleCasePipe,
    provideAnimations(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
})
export class AppModule {}
